import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';

import useAuth from '@/hooks/useAuth';

import ReportsCompliancePlanCard from '@/components/Cards/PlanCard/PlanCards/ReportsCompliancePlanCard';
import ReportsPlanCard from '@/components/Cards/PlanCard/PlanCards/ReportsPlanCard';
import LoadMorePaginator from '@/components/LoadMorePaginator/LoadMorePaginator';
import NoMessage from '@/components/NoMessage/NoMessage';

import { DataViewKey, useDataView } from '@/atoms/data-view';
import { filtersToFilterInValues } from '@/atoms/data-view/helpers';
import { licenseeLocationAtom } from '@/atoms/licensee-location';
import { type ReportsPlanRow } from '@/routes/Reports/hooks/useGetPlansSummary';

interface PlansListProps {
  isFetching: boolean;
  dataViewKey: DataViewKey;
}

const PlansList: React.FC<PlansListProps> = ({ dataViewKey, isFetching }) => {
  const { t } = useTranslation();

  const { selected: activeLicenseeLocation } =
    useAtomValue(licenseeLocationAtom);

  const { data: auth } = useAuth();
  const userIsLicenseeStaff = auth?.userIsLicenseeStaff ?? false;

  const [
    {
      display: { total, items },
      filters,
    },
    { showMore },
  ] = useDataView<ReportsPlanRow>(dataViewKey);

  const currentFilters = filtersToFilterInValues(filters);

  const noResultsMessage =
    isFetching || !!total
      ? null
      : userIsLicenseeStaff && !activeLicenseeLocation?.number
        ? t('Licensee.chooseALicenseeLocation')
        : !currentFilters.length && !filters.search
          ? t('Reports.noPlans')
          : t('TrainingPlans.noPlansResults');

  return (
    <div className="relative flex max-w-full grow flex-col">
      {!isFetching && (
        <>
          <div className="flex flex-col gap-4">
            {items.map(plan =>
              plan.legacyEnrollments ? (
                <ReportsCompliancePlanCard
                  plan={plan}
                  enrollments={plan.legacyEnrollments}
                  isEnabled={plan.isEnabled}
                  key={plan.id}
                />
              ) : (
                <ReportsPlanCard
                  assignedUsers={plan.assignedUsers ?? 0}
                  completedUsers={plan.completedUsers ?? 0}
                  key={plan.id}
                  plan={plan}
                />
              ),
            )}
          </div>
          <LoadMorePaginator
            onClick={showMore}
            showing={items.length}
            showingText={t('TrainingPlans.showingXOfYPlans', {
              showing: items.length,
              total: total,
            })}
            total={total}
          />
        </>
      )}

      {noResultsMessage && <NoMessage message={noResultsMessage} />}
    </div>
  );
};

export default PlansList;
